import { siteApi } from "@api/index";
import { toast } from "@components/utils/Toaster";
import { SEON_FEATURE } from "@configs/seon";
import { useStatsigTracking } from "@configs/statsig";
import { DEFAULT_ERROR_MESSAGE } from "@constants/misc";
import { useWSSecretCodeUpdated } from "@hooks/useAppWS";
import { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import {
  getCodesClaimedCount,
  hasClaimedAsSharee,
  hasSecretCodeStatus,
  isSharer,
  isSharerVisible,
  mapSecretCodeError,
  normalizeSecretCode,
} from "@utils/secret-code";
import { SecretCodeState } from "@views/PromotionSecretCodePage/types";
import {
  APIErrorCodes,
  BN_ZERO,
  ErrorResponse,
  QueryError,
  SecretCodeCampaign,
  SecretCodeCampaignParticipant,
  SecretCodeCampaignParticipantStatus,
  SecretCodeStatus,
  WalletTxMethod,
  bnOrZero,
} from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import usePreference from "./usePreference";
import { useSeonSession } from "./useSeonSession";
import useUserMode from "./useUserMode";

interface RedeemCodeOptions {
  onSuccess?: (amount: number) => void;
  onError?: (errorCode: string) => void;
}

interface ClaimRewardOptions {
  onSuccess?: () => void;
  onError?: (errorCode: string) => void;
}

interface UseSecretCodeStatusResult {
  shouldShowGiverSecretCode: boolean;
  shouldShowTakerSecretCode: boolean;
  giver?: {
    status: SecretCodeState;
    code?: string;
    claimAmount?: number;
    friendClaimAmount?: number;
    claimedCodeCount?: number;
    totalCodeCount?: number;
  };
  taker?: {
    status: SecretCodeState;
    lockedInstructions?: Record<string, string>;
  };
  isAvailable: boolean;
  isFetching: boolean;
  details?: {
    remainingSeconds: number;
  };
  refetch: () => void;
  redeemCode: (code: string, options?: RedeemCodeOptions) => Promise<void>;
  claimReward: (options?: ClaimRewardOptions) => Promise<void>;
  isRedeeming: boolean;
  isClaiming: boolean;
  isGiverSecretCodeUnseen: boolean;
  markGiverSecretCodeAsSeen: () => void;
  isTakerIneligible: boolean;
}

/**
 * Calculate remaining seconds until campaign end
 */
function calculateRemainingSeconds(endDate: moment.Moment | null | undefined): number {
  if (!endDate) return 0;
  const diff = moment(endDate).diff(moment(), "seconds");
  return Math.max(0, diff);
}

/**
 * Handle claim reward errors with toast notifications
 */
function handleClaimRewardError(
  error: QueryError<ErrorResponse> | unknown,
  t: (key: string) => string,
  onError?: (errorCode: string) => void,
): void {
  const errorObj = error as QueryError<ErrorResponse>;
  const errorMessage = errorObj?.data?.error?.message || DEFAULT_ERROR_MESSAGE;

  // Handle specific error codes - show warning toast for systematic behaviors
  if (errorMessage === APIErrorCodes.SecretCode.AlreadyClaimed) {
    toast.warning(t("bonus already claimed"));
    onError?.(errorMessage);
    return;
  }

  toast.warning(t("something went wrong"));
  onError?.(errorMessage);
}

/**
 * Transform SDK participant status to application SecretCodeState
 */
function getGiverState(
  participant: SecretCodeCampaignParticipant | null | undefined,
  campaign: SecretCodeCampaign | null | undefined,
): SecretCodeState {
  // if participant is fully redeemed, by pass the check
  if (participant?.status === SecretCodeCampaignParticipantStatus.FullyRedeemed) {
    return SecretCodeState.CLAIMABLE;
  }

  if (!participant || !campaign) return SecretCodeState.UNAVAILABLE;

  // Check if campaign is expired
  if (campaign.endDate && moment(campaign.endDate).isBefore(moment())) {
    return SecretCodeState.EXPIRED;
  }

  // Map participant status to SecretCodeState
  switch (participant.status) {
    case SecretCodeCampaignParticipantStatus.Issued:
      return SecretCodeState.PENDING;
    case SecretCodeCampaignParticipantStatus.Expired:
      return SecretCodeState.EXPIRED;
    case SecretCodeCampaignParticipantStatus.Completed:
      // Already completed, hide from UI
      return SecretCodeState.UNAVAILABLE;
    default:
      return SecretCodeState.UNAVAILABLE;
  }
}

/**
 * Transform SDK status to application SecretCodeState for taker (sharee)
 * Status checks in order: isFriendVisible → lockedInstructions → secretCodeTx → campaign
 */
export function getTakerState(status: SecretCodeStatus | null | undefined): {
  state: SecretCodeState;
  lockedInstructions?: Record<string, string>;
} {
  if (!status) {
    return { state: SecretCodeState.UNAVAILABLE };
  }

  // 1. Check isFriendVisible
  if (status.isFriendVisible === false) {
    return { state: SecretCodeState.UNAVAILABLE };
  }

  // 2. Check lockedInstructions
  if (status.lockedInstructions) {
    return {
      state: SecretCodeState.LOCKED,
      lockedInstructions: status.lockedInstructions as Record<string, string>,
    };
  }

  // 3. Check secretCodeTx (already claimed)
  if (hasClaimedAsSharee(status.secretCodeTx)) {
    return { state: SecretCodeState.REDEEMED };
  }

  // All checks passed - can redeem
  return { state: SecretCodeState.REDEEMABLE };
}

export function useSecretCodeStatus(): UseSecretCodeStatusResult {
  const { isGuest } = useUserMode();
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { seenGiverSecretCodeCode } = usePreference();
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  const {
    data: rewardStatus,
    isLoading: isFetchingRewardStatus,
    refetch: refetchRewardStatus,
  } = siteApi.useGetRewardsStatusQuery(undefined, {
    skip: isGuest,
  });

  const [claimRewardMutation] = siteApi.useClaimRewardMutation();
  const { getSession: getSeonSession } = useSeonSession(SEON_FEATURE.PROMOTION_SECRET_CODE);
  const { trackPromoClaimAttempt, trackPromoClaimSuccess } = useStatsigTracking();

  // WebSocket integration
  useWSSecretCodeUpdated({
    cb: () => {
      refetchRewardStatus();
    },
  });

  // Extract secret-code status
  const secretCodeStatus = useMemo(() => {
    return hasSecretCodeStatus(rewardStatus) ? rewardStatus["secret-code"] : null;
  }, [rewardStatus]);

  const handleRedeemCode = useCallback(
    async (code: string, options?: RedeemCodeOptions): Promise<void> => {
      setIsRedeeming(true);
      try {
        const normalizedCode = normalizeSecretCode(code);
        const seonSession = await getSeonSession();

        trackPromoClaimAttempt(WalletTxMethod.SecretCodeSharee);

        const claimRewardRes = await claimRewardMutation({
          seonSession,
          query: { type: WalletTxMethod.SecretCodeSharee },
          secretCode: normalizedCode,
        });

        if ("error" in claimRewardRes) {
          const error = claimRewardRes.error as QueryError<ErrorResponse>;
          const errorMessage = error?.data?.error?.message || DEFAULT_ERROR_MESSAGE;
          const mappedError = mapSecretCodeError(errorMessage);
          options?.onError?.(mappedError);
          return;
        }

        trackPromoClaimSuccess(WalletTxMethod.SecretCodeSharee);

        // Extract amount from response using bnOrZero utility for BigNumber handling
        const netAmount = claimRewardRes.data?.model?.netAmount;
        const amount = bnOrZero(netAmount).toNumber();
        await refetchRewardStatus();
        options?.onSuccess?.(amount);
      } catch (error) {
        const errorMessage = (error as QueryError<ErrorResponse>)?.data?.error?.message || DEFAULT_ERROR_MESSAGE;
        const mappedError = mapSecretCodeError(errorMessage);
        options?.onError?.(mappedError);
      } finally {
        setIsRedeeming(false);
      }
    },
    [claimRewardMutation, getSeonSession, refetchRewardStatus, trackPromoClaimAttempt, trackPromoClaimSuccess],
  );

  const handleClaimReward = useCallback(
    async (options?: ClaimRewardOptions): Promise<void> => {
      setIsClaiming(true);
      try {
        const seonSession = await getSeonSession();

        trackPromoClaimAttempt(WalletTxMethod.SecretCodeSharer);

        const claimRewardRes = await claimRewardMutation({
          seonSession,
          query: { type: WalletTxMethod.SecretCodeSharer },
        });

        if ("error" in claimRewardRes) {
          handleClaimRewardError(claimRewardRes.error, t, options?.onError);
          return;
        }

        trackPromoClaimSuccess(WalletTxMethod.SecretCodeSharer);

        await refetchRewardStatus();
        options?.onSuccess?.();
      } catch (error) {
        handleClaimRewardError(error, t, options?.onError);
      } finally {
        setIsClaiming(false);
      }
    },
    [claimRewardMutation, getSeonSession, refetchRewardStatus, t, trackPromoClaimAttempt, trackPromoClaimSuccess],
  );

  const {
    shouldShowGiverSecretCode,
    shouldShowTakerSecretCode,
    giver,
    taker,
    isAvailable,
    details,
    isGiverSecretCodeUnseen,
    isTakerIneligible,
  } = useMemo(() => {
    if (!secretCodeStatus) {
      return {
        shouldShowGiverSecretCode: false,
        shouldShowTakerSecretCode: false,
        giver: undefined,
        taker: undefined,
        isAvailable: false,
        details: undefined,
        isGiverSecretCodeUnseen: false,
        isTakerIneligible: false,
      };
    }

    // Get current participant OR sum of unclaimed participant
    const participant = getParticipantStatus(secretCodeStatus.participant, secretCodeStatus.claimableParticipants);
    const campaign = participant?.secretCodeCampaign;

    // Check if feature is available (sharer is visible, including expired campaigns within grace period)
    const enabled = isSharerVisible(participant);

    // Giver (Sharer) flow
    const isSharerMember = isSharer(participant);
    const giverState = getGiverState(participant, campaign);
    const giverCode = participant?.code;
    const codesClaimed = getCodesClaimedCount(participant);
    const totalCodes = participant?.codesGenerated ?? 0;
    const sharerReward = participant?.rewardAmt;
    const claimAmount = bnOrZero(sharerReward).toNumber();
    // Use campaign.codeReward for sharee/taker reward amount (reward per code claim)
    const codeReward = campaign?.codeReward;
    const friendClaimAmount = bnOrZero(codeReward).toNumber();

    const shouldShowGiver = enabled && isSharerMember && giverState !== SecretCodeState.UNAVAILABLE;
    const isGiverSecretCodeUnseen =
      enabled && !!giverCode && giverCode !== seenGiverSecretCodeCode && giverState === SecretCodeState.PENDING;

    // Taker (Sharee) flow - always show if secret code status exists and taker state is not unavailable
    const takerStateData = getTakerState(secretCodeStatus);
    const shouldShowTaker = takerStateData.state !== SecretCodeState.UNAVAILABLE;
    const isTakerIneligible = takerStateData.state === SecretCodeState.LOCKED;

    // Calculate remaining seconds from campaign endDate
    const remainingSeconds = calculateRemainingSeconds(campaign?.endDate);

    const result = {
      shouldShowGiverSecretCode: shouldShowGiver,
      shouldShowTakerSecretCode: shouldShowTaker,
      giver: shouldShowGiver
        ? {
            status: giverState,
            code: giverCode,
            claimAmount,
            friendClaimAmount,
            claimedCodeCount: codesClaimed,
            totalCodeCount: totalCodes,
          }
        : undefined,
      taker: shouldShowTaker
        ? {
            status: takerStateData.state,
            lockedInstructions: takerStateData.lockedInstructions,
          }
        : undefined,
      isAvailable: enabled,
      details: {
        remainingSeconds,
      },
      isGiverSecretCodeUnseen,
      isTakerIneligible,
    };

    return result;
  }, [secretCodeStatus, seenGiverSecretCodeCode]);

  const markGiverSecretCodeAsSeen = useCallback(() => {
    const currentCode = giver?.code;

    if (currentCode) {
      dispatch(preferenceSlice.actions.setSeenGiverSecretCodeCode(currentCode));
    }
  }, [dispatch, giver?.code]);

  if (isGuest) {
    return {
      shouldShowGiverSecretCode: false,
      shouldShowTakerSecretCode: true,
      giver: undefined,
      taker: undefined,
      isAvailable: false,
      isFetching: false,
      details: undefined,
      refetch: () => {},
      redeemCode: async () => {},
      claimReward: async () => {},
      isRedeeming: false,
      isClaiming: false,
      isGiverSecretCodeUnseen: false,
      markGiverSecretCodeAsSeen: () => {},
      isTakerIneligible: false,
    };
  }

  return {
    shouldShowGiverSecretCode,
    shouldShowTakerSecretCode,
    giver,
    taker,
    isAvailable,
    isFetching: isFetchingRewardStatus,
    details,
    refetch: refetchRewardStatus,
    redeemCode: handleRedeemCode,
    claimReward: handleClaimReward,
    isRedeeming,
    isClaiming,
    isGiverSecretCodeUnseen,
    markGiverSecretCodeAsSeen,
    isTakerIneligible,
  };
}

/**
 * A sharer can have multiple claimable campaigns.
 * "participant" is the current campaign participant status
 * "claimableParticipants" is the list of claimable campaigns
 *
 * We prefer to show claimable campaign, status will be the sum of all claimable campaigns:
 * - rewardAmt: sum of all claimable campaigns
 * - codesGenerated: sum of all claimable campaigns
 * - redeemed codes: equals to codesGenerated (because all codes are claimable)
 *
 * If there is no claimable campaign, return the current participant status
 */
const getParticipantStatus = (
  participant: SecretCodeCampaignParticipant | null | undefined,
  claimableParticipants: SecretCodeCampaignParticipant[],
): SecretCodeCampaignParticipant | null | undefined => {
  let combinedParticipant: SecretCodeCampaignParticipant | null | undefined = undefined;

  // if sharer has pending claimable campaigns, return the combined participant status
  if (claimableParticipants && claimableParticipants.length > 0) {
    let representativeParticipant = claimableParticipants[0];
    let totalRewardAmt = BN_ZERO;
    let totalCodesGenerated = 0;
    for (const participant of claimableParticipants) {
      totalRewardAmt = totalRewardAmt.plus(bnOrZero(participant.rewardAmt));
      totalCodesGenerated = totalCodesGenerated + participant.codesGenerated;
    }
    // update the representative participant with the combined values
    representativeParticipant = {
      ...representativeParticipant,
      rewardAmt: totalRewardAmt,
      codesGenerated: totalCodesGenerated,
    };
    combinedParticipant = representativeParticipant;
  } else {
    combinedParticipant = participant;
  }

  return combinedParticipant;
};
